import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import BlockRenderer from "@/components/brand/BlockRenderer";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useI18n } from "@/contexts/I18nContext";
import { pageLabel } from "@/i18n/pageLabel";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  ChevronDown, ChevronUp, Copy, Trash2, Pencil, Undo2, Redo2, Plus, X,
  Type, AlignLeft, Columns, Image as ImageIcon, Images, Link as LinkIcon,
  Palette as PaletteIcon, Download, Aperture, Minus,
} from "lucide-react";

const BLOCK_TYPES = [
  { type: "heading", label: "Titel", labelKey: "settings.editor_block_heading", drawerIcon: Type, drawerAccent: "A|", defaults: { level: 1, text: "" } },
  { type: "title_text_2col", label: "Titel en tekst", labelKey: "settings.editor_block_title_text", drawerIcon: Columns, defaults: { title: "", text: "" } },
  { type: "core_values", label: "Kernwaarden", labelKey: null, drawerIcon: Columns, defaults: { title: "Kernwaarden", items: [] } },
  { type: "characteristics", label: "Karakteristieken", labelKey: null, drawerIcon: Columns, defaults: { title: "Karakteristieken", items: [] } },
  { type: "audience", label: "Doelgroep", labelKey: null, drawerIcon: Columns, defaults: { title: "Doelgroep", items: [] } },
  { type: "dos_donts", label: "Do's & don'ts", labelKey: null, drawerIcon: Columns, defaults: { title: "Do's & don'ts", dos: [], donts: [] } },
  { type: "dictionary", label: "Woordenboek", labelKey: null, drawerIcon: Columns, defaults: { title: "Woordenboek", use_words: [], avoid_words: [], examples: [] } },
  { type: "image_hero", label: "Hero afbeelding", labelKey: "settings.editor_block_hero_image", drawerIcon: ImageIcon, defaults: { file_id: null, caption: "", bg_color: "#b7cff7" } },
  { type: "image", label: "Afbeelding", labelKey: "settings.editor_block_image", drawerIcon: ImageIcon, defaults: { file_id: null, caption: "" } },
  { type: "text", label: "Tekst", labelKey: "settings.editor_block_text", drawerIcon: AlignLeft, defaults: { text: "" } },
  { type: "button", label: "Knop", labelKey: "settings.editor_block_button", drawerIcon: LinkIcon, defaults: { label: "", href: "", style: "primary" } },
  { type: "image_2col", label: "Twee afbeeldingen", labelKey: "settings.editor_block_two_images", drawerIcon: Images, defaults: { left: { file_id: null, caption: "" }, right: { file_id: null, caption: "" } } },
  { type: "image_grid", label: "Afbeeldingenraster", labelKey: "settings.editor_block_image_grid", drawerIcon: Images, defaults: { title: "", description: "", items: [] } },
  { type: "color_palette", label: "Kleuren", labelKey: "settings.editor_block_colors", drawerIcon: PaletteIcon, defaults: { colors: [{ name: "", hex: "#fe7f57" }] } },
  { type: "typography", label: "Lettertype", labelKey: "settings.editor_block_typography", drawerIcon: Type, defaults: { family: "", description: "", font_files: [], styles: [] } },
  { type: "logo", label: "Logo", labelKey: "settings.editor_block_logo", drawerIcon: Aperture, defaults: { file_id: null, description: "" } },
  // Logo-page bespoke block types. `logo_variant` doubles for Primaire + Secundaire
  // logo (same component, different title) and is also reachable via the drawer.
  { type: "logo_variant",     label: "Logo variant", labelKey: "settings.editor_block_logo_variant", drawerIcon: Aperture, defaults: { title: "", text: "", file_id: null, filename: "", bg_color: "#f2f1ed" } },
  { type: "logo_avatar",      label: "Avatar",       labelKey: null, drawerIcon: Aperture,    defaults: { title: "Avatar", text: "", items: [] } },
  { type: "logo_clear_space", label: "Vrije ruimte", labelKey: null, drawerIcon: Aperture,    defaults: { title: "Vrije ruimte", text: "", top: 24, right: 24, bottom: 24, left: 24, unit: "px" } },
  { type: "logo_color_usage", label: "Kleurgebruik", labelKey: null, drawerIcon: PaletteIcon, defaults: { title: "Kleurgebruik", text: "", items: [] } },
  { type: "logo_donts",       label: "Don'ts",       labelKey: null, drawerIcon: Aperture,    defaults: { title: "Don'ts", text: "", items: [] } },
  { type: "download", label: "Download", labelKey: "settings.editor_block_download", drawerIcon: Download, defaults: { label: "", description: "", file_id: null, filename: "" } },
  { type: "divider", label: "Scheiding", labelKey: "settings.editor_block_divider", drawerIcon: Minus, defaults: {} },
];

const newId = () => (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));

const tocFromBlocks = (blocks) => {
  const items = [];
  for (const b of blocks || []) {
    if (b.type === "title_text_2col" && b.content?.title) items.push({ id: b.id, text: b.content.title });
    else if ((b.type === "core_values" || b.type === "characteristics" || b.type === "audience") && b.content?.title) items.push({ id: b.id, text: b.content.title });
    else if (b.type === "dos_donts") items.push({ id: b.id, text: b.content?.title || "Do's & don'ts" });
    else if (b.type === "dictionary") items.push({ id: b.id, text: b.content?.title || "Woordenboek" });
    else if (b.type === "heading" && b.content?.level && b.content.level >= 2 && b.content.text) items.push({ id: b.id, text: b.content.text });
    else if (b.type === "typography" && b.content?.family) items.push({ id: b.id, text: b.content.family });
    else if (b.type === "image_grid" && b.content?.title) items.push({ id: b.id, text: b.content.title });
  }
  return items;
};

// Guided build sequence for the "Merkstrategie" / "Brand strategy" page.
// Steps 1-4 insert a standard Sectietitel (title_text_2col) with the title
// pre-filled; step 5 inserts the custom core-values block.
const STRATEGY_STEPS = [
  { title: "Merkverhaal",  blockType: "title_text_2col" },
  { title: "Purpose",      blockType: "title_text_2col" },
  { title: "Visie",        blockType: "title_text_2col" },
  { title: "Missie",       blockType: "title_text_2col" },
  { title: "Kernwaarden",  blockType: "core_values" },
];

// Guided build sequence for the "Tone of voice" page. Mixes title_text_2col,
// core-values layout (for Karakteristieken + Doelgroep) and two bespoke
// blocks (dos_donts, dictionary).
const TOV_STEPS = [
  { title: "Persoonlijkheid",  blockType: "title_text_2col" },
  { title: "Karakteristieken", blockType: "characteristics" },
  { title: "Toon",             blockType: "title_text_2col" },
  { title: "Do's & don'ts",    blockType: "dos_donts" },
  { title: "Woordenboek",      blockType: "dictionary" },
  { title: "Doelgroep",        blockType: "audience" },
];

// Guided sequence for the "Logo" page. First two steps reuse the same
// logo_variant block (same component, different title); the remaining four
// insert bespoke blocks that each have their own custom renderer.
const LOGO_STEPS = [
  { title: "Primaire logo",    blockType: "logo_variant" },
  { title: "Secundaire logo",  blockType: "logo_variant" },
  { title: "Avatar",           blockType: "logo_avatar" },
  { title: "Vrije ruimte",     blockType: "logo_clear_space" },
  { title: "Kleurgebruik",     blockType: "logo_color_usage" },
  { title: "Don'ts",           blockType: "logo_donts" },
];

const PAGE_STEPS = {
  "Merkstrategie": STRATEGY_STEPS,
  "Brand strategy": STRATEGY_STEPS,
  "Tone of voice": TOV_STEPS,
  "Tone-of-voice": TOV_STEPS,
  "Logo": LOGO_STEPS,
};

// Make a step title (e.g. "Do's & don'ts") safe to use inside a data-testid
// attribute. We lower-case, strip anything that isn't a letter/digit and join
// the remaining words with single dashes — so Playwright locators stay stable
// regardless of punctuation or whitespace in the title.
const slugifyStep = (title) =>
  (title || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const guidedNextStep = (pageName, blocks, skipped) => {
  const steps = PAGE_STEPS[pageName];
  if (!steps) return null;
  const present = new Set();
  for (const b of blocks || []) {
    const title = (b.content?.title || "").trim().toLowerCase();
    if (title) present.add(title);
  }
  const skippedSet = new Set((skipped || []).map((s) => s.toLowerCase()));
  return steps.find((s) => {
    const key = s.title.toLowerCase();
    return !present.has(key) && !skippedSet.has(key);
  }) || null;
};

// A page is "guided" when it drives the sequential add-section placeholder
// (and shows "Nog een sectie toevoegen" once the sequence is complete).
const isGuidedPage = (pageName) => Boolean(PAGE_STEPS[pageName]);

// Per-page drawer filter: when set, the Add-section drawer only shows these
// block types in this exact order. Pages not listed show every BLOCK_TYPES
// entry. Keeps the bespoke guided blocks (core_values, dos_donts, …) OUT of
// the drawer: those are inserted exclusively via the guided placeholder.
const DRAWER_ORDER_BY_PAGE = {
  "Merkstrategie":  ["heading", "title_text_2col", "text"],
  "Brand strategy": ["heading", "title_text_2col", "text"],
  "Tone of voice":  ["heading", "title_text_2col", "text"],
  "Tone-of-voice":  ["heading", "title_text_2col", "text"],
  "Logo":           ["heading", "title_text_2col", "text", "logo_variant", "image"],
};

export default function PageEditor({ page, brand, editMode, setEditMode, canEdit, onChange, onDelete, onDuplicate }) {
  const { t } = useI18n();
  const [name, setName] = useState(page.name);
  const [intro, setIntro] = useState(page.intro || "");
  const [blocks, setBlocks] = useState(page.blocks || []);
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [focusBlockId, setFocusBlockId] = useState(null);
  // Steps the user dismissed via "Overslaan", ordered as a stack so "Terug"
  // can un-skip the most recent one. Resets per page.id.
  const [skippedSteps, setSkippedSteps] = useState([]);
  // Track unsaved edits so the Save button can switch between muted (no
  // changes) and solid-black (dirty). Autosave in persist() will reset this
  // to false once the backend confirms the write.
  const [dirty, setDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [tickNow, setTickNow] = useState(() => Date.now());
  const saveTimer = useRef(null);

  // Contrast helper: dark header → white text, light header → near-black.
  const hexContrast = (hex) => {
    const h = (hex || "").replace("#", "");
    if (h.length !== 6) return "#1a1a1a";
    const toLin = (c) => {
      const v = parseInt(c, 16) / 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    };
    const L = 0.2126 * toLin(h.slice(0, 2)) + 0.7152 * toLin(h.slice(2, 4)) + 0.0722 * toLin(h.slice(4, 6));
    return L > 0.5 ? "#1a1a1a" : "#ffffff";
  };
  const headerBg = brand?.color_header_bg || "#b7cff7";
  const headerFg = hexContrast(headerBg);
  const headerFgIsLight = headerFg === "#ffffff";

  useEffect(() => { setName(page.name); setIntro(page.intro || ""); setBlocks(page.blocks || []); setHistory([]); setFuture([]); setSkippedSteps([]); setDirty(false); setLastSavedAt(null);
    // We intentionally only re-sync on page.id change; page.name/intro/blocks are initial seeds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page.id]);

  // Keyboard: Cmd/Ctrl + S saves and exits edit mode
  useEffect(() => {
    if (!editMode) return;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "s" || e.key === "S")) {
        e.preventDefault();
        forceSave();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, blocks, name]);

  // Scroll + focus newly added block
  useEffect(() => {
    if (!focusBlockId) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`block-${focusBlockId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        const input = el.querySelector('input:not([type="color"]):not([type="file"]), textarea, [contenteditable="true"]');
        if (input) {
          input.focus();
          if (input.setSelectionRange && typeof input.value === "string") {
            try { input.setSelectionRange(input.value.length, input.value.length); } catch { /* ignore */ }
          }
        }
      }
      setFocusBlockId(null);
    }, 180);
    return () => clearTimeout(t);
  }, [focusBlockId]);

  const toc = useMemo(() => tocFromBlocks(blocks), [blocks]);

  // Flag any change as dirty so the Save button lights up. Actual persistence
  // runs on an interval (every 60s) in edit mode, or when the user clicks Save.
  const persist = () => { setDirty(true); };

  // Keep latest state in a ref so the autosave interval reads fresh values
  // without us having to re-schedule the interval on every keystroke.
  const stateRef = useRef({ blocks, name, intro, dirty });
  useEffect(() => {
    stateRef.current = { blocks, name, intro, dirty };
  }, [blocks, name, intro, dirty]);

  // Autosave every minute while in edit mode, and tick the "Saved · xx min
  // geleden" label every 15s so it stays accurate without being noisy.
  useEffect(() => {
    if (!editMode) return undefined;
    const saveId = setInterval(async () => {
      const s = stateRef.current;
      if (!s.dirty) return;
      try {
        await onChange({ blocks: s.blocks, name: s.name, intro: s.intro });
        setDirty(false);
        setLastSavedAt(Date.now());
      } catch { /* keep dirty so the next tick retries */ }
    }, 60000);
    const tickId = setInterval(() => setTickNow(Date.now()), 15000);
    return () => { clearInterval(saveId); clearInterval(tickId); };
  }, [editMode, onChange]);

  const commit = (nextBlocks) => {
    setHistory((h) => [...h, blocks].slice(-30));
    setFuture([]);
    setBlocks(nextBlocks);
    // Belt-and-suspenders: persist() already flips dirty but we also set it
    // here directly so structural changes (add/duplicate/delete section,
    // move up/down) activate the Save button even if the autosave later
    // flushes it back to clean.
    setDirty(true);
    persist(nextBlocks);
  };

  const undo = () => {
    if (!history.length) return;
    const prev = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setFuture((f) => [blocks, ...f]);
    setBlocks(prev);
    persist(prev);
  };
  const redo = () => {
    if (!future.length) return;
    const next = future[0];
    setFuture((f) => f.slice(1));
    setHistory((h) => [...h, blocks].slice(-30));
    setBlocks(next);
    persist(next);
  };

  const updateBlock = (id, content) => {
    setBlocks((bs) => {
      const next = bs.map((b) => (b.id === id ? { ...b, content: { ...b.content, ...content } } : b));
      persist(next);
      return next;
    });
  };

  const moveBlock = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= blocks.length) return;
    const next = [...blocks];
    [next[index], next[target]] = [next[target], next[index]];
    commit(next);
  };
  const duplicateBlock = (index) => {
    const clone = { ...blocks[index], id: newId(), content: JSON.parse(JSON.stringify(blocks[index].content)) };
    commit([...blocks.slice(0, index + 1), clone, ...blocks.slice(index + 1)]);
  };
  const deleteBlock = (index) => commit(blocks.filter((_, i) => i !== index));
  const appendBlock = (type) => {
    const meta = BLOCK_TYPES.find((t) => t.type === type);
    const block = { id: newId(), type, content: JSON.parse(JSON.stringify(meta.defaults)) };
    commit([...blocks, block]);
    setDrawerOpen(false);
    setFocusBlockId(block.id);
  };

  // Guided-sequence insert. Pre-fills the block title with the step name and
  // seeds the shape for list-style blocks so the user sees empty rows.
  const insertStrategyStep = (step) => {
    const meta = BLOCK_TYPES.find((b) => b.type === step.blockType);
    const content = JSON.parse(JSON.stringify(meta.defaults));
    content.title = step.title;
    if (step.blockType === "core_values" || step.blockType === "characteristics" || step.blockType === "audience") {
      content.items = [{ id: newId(), name: "", text: "" }];
    } else if (step.blockType === "dos_donts") {
      content.dos = [{ id: newId(), text: "" }];
      content.donts = [{ id: newId(), text: "" }];
    } else if (step.blockType === "dictionary") {
      content.use_words = [{ id: newId(), text: "" }];
      content.avoid_words = [{ id: newId(), text: "" }];
      content.examples = [{ id: newId(), text: "" }];
    } else if (step.blockType === "logo_avatar") {
      content.items = [{ id: newId(), file_id: null, description: "" }];
    } else if (step.blockType === "logo_color_usage") {
      content.items = [{ id: newId(), logo_color: "", bg_color: "" }];
    } else if (step.blockType === "logo_donts") {
      content.items = [{ id: newId(), transform: "", text: "" }];
    } else if (step.blockType === "logo_variant") {
      // Flag which variant this is so Clear-space / Color-usage / Don'ts can
      // reliably pick up the Primaire logo even if the user re-titles it later.
      content.variant = step.title === "Primaire logo" ? "primary"
                      : step.title === "Secundaire logo" ? "secondary"
                      : undefined;
    }
    const id = newId();
    const nextBlock = { id, type: step.blockType, content };
    commit([...blocks, nextBlock]);
    setFocusBlockId(id);
  };

  const forceSave = async () => {
    clearTimeout(saveTimer.current);
    try {
      await onChange({ blocks, name, intro });
      setDirty(false);
      setLastSavedAt(Date.now());
      toast.success("Opgeslagen");
    } catch { toast.error(t("settings.editor_save_failed")); }
  };

  // Human-readable Save-button label. Switches between "Save" (dirty, black),
  // "Saved · nu / Xm geleden" (clean, white) and the default disabled Save.
  let saveLabel = "Save";
  if (!dirty && lastSavedAt) {
    const diffSec = Math.max(0, Math.floor((tickNow - lastSavedAt) / 1000));
    if (diffSec < 10) saveLabel = "Saved · nu";
    else if (diffSec < 60) saveLabel = `Saved · ${diffSec}s geleden`;
    else {
      const min = Math.floor(diffSec / 60);
      saveLabel = `Saved · ${min} min geleden`;
    }
  }

  return (
    <div className="min-h-full flex flex-col">
      {/* STICKY EDIT TOOLBAR — original floating pill, but anchored via a
          zero-height sticky wrapper so it stays visible at the top-right of
          the viewport while the user scrolls through long pages. */}
      {editMode && (
        <div className="sticky top-0 z-50 pointer-events-none bg-transparent">
          <div className={`transition-all duration-300 ease-in-out ${drawerOpen ? "ml-[240px]" : "ml-0"}`}>
            <div className="flex items-center justify-end gap-2 px-6 pt-5 pb-4 pointer-events-none">
              <button
                data-testid="undo-btn"
                onClick={undo}
                disabled={!history.length}
                title="Undo"
                className="pointer-events-auto h-9 w-9 flex items-center justify-center rounded-lg bg-white border border-gray-200 shadow-sm hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Undo2 className="w-4 h-4" />
              </button>
              <button
                data-testid="redo-btn"
                onClick={redo}
                disabled={!future.length}
                title="Redo"
                className="pointer-events-auto h-9 w-9 flex items-center justify-center rounded-lg bg-white border border-gray-200 shadow-sm hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Redo2 className="w-4 h-4" />
              </button>
              <button
                data-testid="save-btn"
                onClick={forceSave}
                disabled={!dirty}
                aria-disabled={!dirty}
                className={`pointer-events-auto h-9 px-4 rounded-lg shadow-sm text-sm font-medium transition-colors ${
                  dirty
                    ? "bg-black text-white hover:bg-black/90 border border-black"
                    : lastSavedAt
                      ? "bg-white text-gray-500 border border-gray-200 cursor-default"
                      : "bg-white text-gray-400 border border-gray-200 cursor-not-allowed"
                }`}
              >
                {saveLabel}
              </button>
              <button
                data-testid="add-section-btn"
                onClick={() => setDrawerOpen(true)}
                className="pointer-events-auto h-9 px-4 inline-flex items-center rounded-lg bg-white border border-gray-200 shadow-sm text-sm font-medium text-[#1a1a1a] hover:bg-gray-50"
              >
                {t("settings.editor_add_section")}
              </button>
              <button
                data-testid="exit-edit-btn"
                onClick={() => { setEditMode(false); setDrawerOpen(false); }}
                className="pointer-events-auto h-9 w-9 inline-flex items-center justify-center rounded-lg bg-white border border-gray-200 shadow-sm hover:bg-gray-50"
                title="Sluit edit mode"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pushable content wrapper — slides right when drawer opens */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ease-in-out ${drawerOpen ? "ml-[240px]" : "ml-0"}`}>
      {/* HERO */}
      <section className="relative" style={{ background: headerBg, color: headerFg }}>
        {/* Top-right controls — view mode only (edit-mode uses sticky toolbar above) */}
        {!editMode && (
          <div className="absolute top-5 right-6 z-10 flex items-center gap-2">
            {canEdit && !page.is_default && (
              <button
                data-testid="delete-page-btn"
                onClick={() => setConfirmDelete(true)}
                className="h-9 px-4 inline-flex items-center gap-2 rounded-lg bg-white border border-gray-200 shadow-sm text-sm font-medium text-red-600 hover:bg-red-50 hover:border-red-200"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete page
              </button>
            )}
            {canEdit && (
              <button
                data-testid="edit-content-btn"
                onClick={() => setEditMode(true)}
                className="h-9 px-4 inline-flex items-center gap-2 rounded-lg bg-white border border-gray-200 shadow-sm text-sm font-medium text-[#1a1a1a] hover:bg-gray-50"
              >
                <Pencil className="w-3.5 h-3.5" /> Edit content
              </button>
            )}
          </div>
        )}

        {/* Hero content — flex so divider sits attached to the left of the ToC links */}
        <div className="flex flex-col md:flex-row min-h-[280px] md:min-h-[320px]">
          <div className="flex-1 flex items-end px-6 md:px-10 pt-16 pb-10 md:pr-8">
            {editMode ? (
              <input
                data-testid="page-title-input"
                value={name}
                onChange={(e) => { setName(e.target.value); persist(null, e.target.value); }}
                className="font-display text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight bg-transparent border-none outline-none w-full"
              />
            ) : (
              <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight" style={{ color: headerFg }} data-testid="page-title">
                {pageLabel(page, t)}
              </h1>
            )}
          </div>
          {toc.length > 0 && (
            <>
              {/* Full-height divider, attached to the left edge of the ToC */}
              <div className="hidden md:block w-px self-stretch shrink-0" style={{ background: headerFgIsLight ? "rgba(255,255,255,0.3)" : "rgba(15,23,42,0.2)" }} aria-hidden="true" />
              <div className="flex items-end px-6 md:pl-8 md:pr-10 pt-6 md:pt-16 pb-10 md:min-w-[280px]">
                <ul className="space-y-0.5" data-testid="toc-list">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <button
                        data-testid={`toc-${t.id}`}
                        onClick={() => document.getElementById(`block-${t.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                        className="text-left text-[15px] hover:underline"
                        style={{ color: headerFg }}
                      >
                        {t.text}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>
      </section>

      {/* INTRO PARAGRAPH */}
      {(editMode || intro) && (
        <div className="max-w-6xl mx-auto px-6 md:px-10 pt-10 md:pt-12 pb-2 w-full">
          {editMode ? (
            <textarea
              data-testid="page-intro-input"
              value={intro}
              onChange={(e) => { setIntro(e.target.value); persist(null, undefined, e.target.value); }}
              placeholder="Introductietekst voor deze pagina…"
              rows={4}
              className="w-full text-lg md:text-xl text-[#1a1a1a] bg-transparent border border-dashed border-gray-200 focus:border-[var(--bh-accent,#fe7f57)] rounded-lg p-4 outline-none focus:outline-none focus-visible:outline-none resize-y min-h-[120px] transition-colors leading-relaxed"
            />
          ) : (
            <p className="text-lg md:text-xl text-[#1a1a1a] leading-relaxed max-w-4xl" data-testid="page-intro">
              {intro}
            </p>
          )}
        </div>
      )}

      {/* Empty-state hint — view mode only. Appears below the intro on pages
          that haven't been filled in yet to steer the owner towards Edit
          content without stuffing the page with placeholder blocks. */}
      {!editMode && blocks.length === 0 && canEdit && (
        <div className="max-w-6xl mx-auto px-6 md:px-10 pt-2 pb-10 w-full">
          <p className="text-sm text-gray-500" data-testid="page-empty-hint">
            {t("settings.editor_empty_view_hint")}
          </p>
        </div>
      )}

      {/* BLOCKS */}
      <div className="flex-1 bg-white pb-[50px]">
        <AnimatePresence initial={false}>
          {blocks.map((block, i) => (
            <motion.div
              key={block.id}
              id={`block-${block.id}`}
              layout
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <hr className="w-full border-t border-gray-200 my-[50px]" />
              <div className="max-w-6xl mx-auto px-10">
                <BlockRenderer block={block} editMode={editMode} brand={brand} pageBlocks={blocks} onUpdate={(c) => updateBlock(block.id, c)} />
                {editMode && (
                  <div className="mt-10 flex items-center gap-3">
                    <button
                      data-testid={`block-down-${i}`}
                      onClick={() => moveBlock(i, 1)}
                      disabled={i === blocks.length - 1}
                      title="Omlaag"
                      className="w-7 h-7 rounded-md bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronDown className="w-3.5 h-3.5 text-gray-700" />
                    </button>
                    <button
                      data-testid={`block-up-${i}`}
                      onClick={() => moveBlock(i, -1)}
                      disabled={i === 0}
                      title="Omhoog"
                      className="w-7 h-7 rounded-md bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronUp className="w-3.5 h-3.5 text-gray-700" />
                    </button>
                    <button
                      data-testid={`block-dup-${i}`}
                      onClick={() => duplicateBlock(i)}
                      title="Dupliceer"
                      className="w-7 h-7 rounded-md bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                    >
                      <Copy className="w-3.5 h-3.5 text-gray-700" />
                    </button>
                    <button
                      data-testid={`block-del-${i}`}
                      onClick={() => deleteBlock(i)}
                      title={t("settings.editor_block_remove_title")}
                      className="w-7 h-7 rounded-md bg-gray-100 hover:bg-red-100 hover:text-red-700 flex items-center justify-center"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {editMode && (() => {
          // Priority:
          //  1. On Merkstrategie, as long as a canonical step is missing AND
          //     the user hasn't skipped it, show the guided placeholder.
          //  2. Else (either page is empty OR the strategy sequence is done),
          //     show the generic placeholder that opens the Add-section drawer.
          const nextStep = guidedNextStep(page.name, blocks, skippedSteps);
          if (nextStep) {
            const key = blocks.length === 0 ? "settings.editor_strategy_first" : "settings.editor_strategy_next";
            const label = t(key).replace("{step}", nextStep.title);
            return (
              <div className="max-w-6xl mx-auto px-10 py-10">
                <button
                  data-testid={`strategy-step-${slugifyStep(nextStep.title)}`}
                  onClick={() => insertStrategyStep(nextStep)}
                  className="w-full py-16 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-gray-400 hover:text-gray-700"
                >
                  <Plus className="w-5 h-5 mx-auto mb-2" /> {label}
                </button>
                <div className="mt-3 flex items-center justify-between gap-4">
                  {/* Subtle progress indicator (bottom-left) — tells the user
                      how far they are in the guided sequence. */}
                  {(() => {
                    const steps = PAGE_STEPS[page.name] || [];
                    const idx = steps.findIndex((s) => s.title === nextStep.title);
                    const total = steps.length;
                    if (!total) return <span />;
                    const current = idx + 1;
                    const pct = Math.round((current / total) * 100);
                    return (
                      <div className="flex items-center gap-2 text-[12px] text-gray-400" data-testid="guided-progress">
                        <span>Stap {current} van {total}</span>
                        <div className="h-0.5 w-24 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-gray-500 transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })()}
                  <div className="flex items-center gap-4">
                    {skippedSteps.length > 0 && (
                      <button
                        data-testid="strategy-back"
                        onClick={() => setSkippedSteps((prev) => prev.slice(0, -1))}
                        className="text-[13px] text-gray-400 hover:text-gray-700 underline-offset-4 hover:underline transition-colors"
                      >
                        {t("settings.editor_strategy_back")}
                      </button>
                    )}
                    <button
                      data-testid={`strategy-skip-${slugifyStep(nextStep.title)}`}
                      onClick={() => setSkippedSteps((prev) => [...prev, nextStep.title.toLowerCase()])}
                      className="text-[13px] text-gray-400 hover:text-gray-700 underline-offset-4 hover:underline transition-colors"
                    >
                      {t("settings.editor_strategy_skip")}
                    </button>
                  </div>
                </div>
              </div>
            );
          }
          // Guided sequence done (or non-guided page) — show generic opener.
          // On a non-guided page we only show it when the page is empty; on a
          // text-only guided page where the sequence has been exhausted we
          // always show it with the "+ Nog een sectie toevoegen" copy so the
          // user can keep adding sections below the guided ones.
          const guided = isGuidedPage(page.name);
          if (blocks.length === 0 || guided) {
            const emptyLabelKey = guided && blocks.length > 0
              ? "settings.editor_strategy_more"
              : "settings.editor_empty_default";
            return (
              <div className="max-w-6xl mx-auto px-10 py-10">
                <button
                  data-testid="empty-add-section"
                  onClick={() => setDrawerOpen(true)}
                  className="w-full py-16 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-gray-400 hover:text-gray-700"
                >
                  <Plus className="w-5 h-5 mx-auto mb-2" /> {t(emptyLabelKey)}
                </button>
              </div>
            );
          }
          return null;
        })()}
      </div>

      {/* ADD SECTION DRAWER — non-modal, pushes main content right */}
      </div>
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen} modal={false}>
        <SheetContent
          side="left"
          hideClose
          overlayClassName="!hidden"
          onInteractOutside={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onFocusOutside={(e) => e.preventDefault()}
          className="!z-[50] !left-0 !top-0 h-screen w-[calc(var(--bh-sidebar-w,16rem)+240px)] !max-w-none !bg-transparent border-0 shadow-xl !py-0 !pr-0 !pl-[var(--bh-sidebar-w,16rem)] flex flex-col backdrop-blur-md !duration-300 data-[state=open]:!duration-300 data-[state=closed]:!duration-300 ease-in-out"
          data-testid="add-section-drawer"
        >
          <div className="absolute inset-0 pointer-events-none" style={{ left: "var(--bh-sidebar-w, 16rem)", background: "linear-gradient(to bottom, rgba(219,209,189,0.4) 0%, rgba(91,137,190,0.4) 50%, rgba(219,209,189,0.4) 100%)" }} />
          <SheetHeader className="relative px-4 pt-10 pb-1 flex-row items-center justify-between space-y-0 shrink-0">
            <SheetTitle data-testid="drawer-title" className="text-[20px] font-medium text-[#1a1a1a] tracking-tight">{t("settings.editor_add_section")}</SheetTitle>
            <button
              data-testid="drawer-close-btn"
              onClick={() => setDrawerOpen(false)}
              className="bg-white rounded-md w-7 h-7 flex items-center justify-center shadow-sm hover:bg-gray-50 text-black outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 focus-visible:ring-0 border-0"
              aria-label="Close"
            >
              <X className="w-3.5 h-3.5 text-black" strokeWidth={2.25} />
            </button>
          </SheetHeader>
          <div className="relative px-3 pb-6 overflow-y-auto bh-scroll flex-1">
            <nav className="space-y-0.5">
              {((DRAWER_ORDER_BY_PAGE[page.name]
                  ? DRAWER_ORDER_BY_PAGE[page.name].map((t) => BLOCK_TYPES.find((bt) => bt.type === t)).filter(Boolean)
                  : BLOCK_TYPES)
              ).map((bt) => (
                <button
                  key={`${bt.type}-${bt.label}`}
                  data-testid={`drawer-${bt.type}`}
                  onClick={() => appendBlock(bt.type)}
                  className="w-full flex items-center gap-2.5 px-3 py-1.5 rounded-md text-[14px] text-[#1a1a1a] hover:bg-white/60 transition-colors text-left"
                >
                  <bt.drawerIcon className="w-4 h-4 shrink-0" strokeWidth={1.75} />
                  <span className="flex-1 truncate">{bt.labelKey ? t(bt.labelKey) : bt.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </SheetContent>
      </Sheet>

      {/* DELETE PAGE CONFIRM */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="delete-page-dialog" className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("settings.editor_delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("settings.editor_delete_body").replace("{name}", page.name)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn" className="rounded-full">{t("settings.editor_delete_cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm-btn"
              onClick={async () => { setConfirmDelete(false); await onDelete?.(); }}
              className="rounded-full bg-red-600 hover:bg-red-700 text-white"
            >
              {t("settings.editor_delete_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
